import React, {
  createContext,
  useState,
  PropsWithChildren,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import noop from 'lodash/noop';
import {
  WebcamScanStep,
} from 'views/EMR/components/WebcamScanDialog/WebcamScanContainer';
import type {
  WebcamOutputMode,
} from 'views/EMR/components/WebcamScanDialog';

type OutputFormat = 'images' | 'pdf';

export type WebcamContextProps = {
    cameraError: string | null;
    isCameraLoading: boolean;
    facingMode: 'user' | 'environment';
    photoCaptures: string[];
    setCameraError: Dispatch<SetStateAction<string | null>>;
    setIsCameraLoading: Dispatch<SetStateAction<boolean>>;
    setFacingMode: Dispatch<SetStateAction<'user' | 'environment'>>;
    setPhotoCaptures: Dispatch<SetStateAction<string[]>>;
    idealSize: { width?: number; height?: number };
    minShot: { w?: number; h?: number };
    setIdealSize: Dispatch<SetStateAction<{ width?: number; height?: number }>>;
    setMinShot: Dispatch<SetStateAction<{ w?: number; h?: number }>>;
    webcamScanStep: WebcamScanStep;
    setWebcamScanStep: Dispatch<SetStateAction<WebcamScanStep>>;
    outputFormat: OutputFormat;
    setOutputFormat: Dispatch<SetStateAction<OutputFormat>>;
    outputMode: WebcamOutputMode;
    multiple?: boolean;
  };

export const WebcamContext = createContext<WebcamContextProps>({
  cameraError: null,
  isCameraLoading: true,
  facingMode: 'environment',
  photoCaptures: [],
  setCameraError: noop,
  setIsCameraLoading: noop,
  setFacingMode: noop,
  setPhotoCaptures: noop,
  idealSize: {},
  minShot: {},
  setIdealSize: noop,
  setMinShot: noop,
  webcamScanStep: 'capture',
  setWebcamScanStep: noop,
  outputFormat: 'images',
  setOutputFormat: noop,
  outputMode: 'select',
  multiple: true,
});

type Props = PropsWithChildren & {
  multiple?: boolean;
  outputMode?: WebcamOutputMode;
};

export default function WebcamProvider({
  children,
  multiple = true,
  outputMode = 'select',
}: Props) {
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(true);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [photoCaptures, setPhotoCaptures] = useState<string[]>([]);
  const [idealSize, setIdealSize] = useState<{ width?: number; height?: number }>({});
  const [minShot, setMinShot] = useState<{ w?: number; h?: number }>({});
  const [webcamScanStep, setWebcamScanStep] = useState<WebcamScanStep>('capture');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(
    outputMode === 'pdf' ? 'pdf' : 'images',
  );

  const value = useMemo(() => ({
    facingMode,
    photoCaptures,
    setFacingMode,
    cameraError,
    isCameraLoading,
    setCameraError,
    setIsCameraLoading,
    setPhotoCaptures,
    idealSize,
    minShot,
    setIdealSize,
    setMinShot,
    webcamScanStep,
    setWebcamScanStep,
    outputFormat,
    setOutputFormat,
    outputMode,
    multiple,
  }), [
    facingMode,
    cameraError,
    isCameraLoading,
    photoCaptures,
    idealSize,
    minShot,
    webcamScanStep,
    setWebcamScanStep,
    outputFormat,
    setOutputFormat,
    outputMode,
    multiple,
  ]);

  return (
    <WebcamContext.Provider value={value}>
      {children}
    </WebcamContext.Provider>
  );
}
